import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { sendRequest } from "../../api";
import Logo from "./Logo";

const RefreshButton = ({ onClick, isLoading, className = "" }) => {
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className={`grow shrink basis-0 p-3 bg-white rounded-md border border-[#e7e6e3] border-solid flex items-center justify-center gap-1.5 hover:bg-[#f5f4f4] disabled:opacity-70 ${className}`}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <RefreshCw className="w-4 h-4" />
      )}
      <span className="text-sm font-medium text-center">
        {isLoading ? 'Refreshing...' : 'Refresh'}
      </span>
    </button>
  );
};

const SelectCategories = () => {
  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [autoReloadTimer, setAutoReloadTimer] = useState(5);

  const fetchCategories = async () => {
    setIsLoading(true);
    setRefreshLoading(true);
    setError(null);
    try {
      const data = await sendRequest("/users/categories");
      
      if (!data || data.length === 0) {
        setCategories([]);
        // Start auto-reload countdown
        let countdown = 5;
        setAutoReloadTimer(countdown);
        const timer = setInterval(() => {
          countdown -= 1;
          setAutoReloadTimer(countdown);
          if (countdown <= 0) {
            clearInterval(timer);
            fetchCategories();
          }
        }, 1000);
        return;
      }

      // Filter out categories with empty names
      const validCategories = data.filter(cat => cat.name && cat.name.trim() !== '');

      // Organize categories into rows of 3
      const organizedCategories = [];
      for (let i = 0; i < validCategories.length; i += 3) {
        organizedCategories.push(validCategories.slice(i, i + 3));
      }
      setCategories(organizedCategories);

      // Set initially enabled categories (only for valid categories)
      const enabledCategories = new Set(
        validCategories
          .filter(cat => cat.publishers.some(pub => pub.enabled))
          .map(cat => cat.id)
      );
      setSelectedCategories(enabledCategories);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
      setRefreshLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const toggleCategory = async (category) => {
    try {
      const isSelected = selectedCategories.has(category.id);
      const publisherUpdates = {};
      
      // Get all publishers in this category and set their enabled status
      category.publishers.forEach(publisher => {
        publisherUpdates[publisher.name] = !isSelected;
      });

      // Update publishers
      await sendRequest("/publishers", "PUT", { publishers: publisherUpdates });

      setSelectedCategories(prev => {
        const newSet = new Set(prev);
        if (newSet.has(category.id)) {
          newSet.delete(category.id);
        } else {
          newSet.add(category.id);
        }
        return newSet;
      });
    } catch (err) {
      console.error('Error toggling category:', err);
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#f8f7f2] p-2">
      <div className="w-full max-w-[702px] flex flex-col">
        <Logo />
        <div className="w-full bg-white rounded-lg shadow-sm border border-[#e7e6e3] flex-col justify-start items-start flex overflow-hidden">
          <div className="self-stretch p-3 justify-between items-center inline-flex">
            <div className="grow shrink basis-0 h-8 justify-start items-center gap-2 flex" />
            <div className="justify-start items-center gap-1 flex">
              <div className="w-8 h-8 bg-[#1b1a17] rounded-full border border-[#1b1a17] flex items-center justify-center">
                <div className="text-white text-base font-bold font-inter">1</div>
              </div>
              <div className="w-8 py-4 flex-col justify-start items-start gap-2.5 inline-flex">
                <div className="self-stretch h-px border border-[#1b1a17]" />
              </div>
              <div className="w-8 h-8 bg-[#1b1a17] rounded-full border border-[#1b1a17] flex items-center justify-center">
                <div className="text-white text-base font-bold font-inter">2</div>
              </div>
              <div className="w-8 py-4 flex-col justify-start items-start gap-2.5 inline-flex">
                <div className="self-stretch h-px border border-[#1b1a17]" />
              </div>
              <div className="w-8 h-8 bg-white rounded-full border border-[#1b1a17] flex items-center justify-center">
                <div className="text-[#1b1a17] text-base font-bold font-inter">3</div>
              </div>
            </div>
            <div className="grow shrink basis-0 h-8 justify-end items-center gap-2 flex" />
          </div>
          <div className="self-stretch px-3 pb-2 flex-col justify-start items-center gap-1 flex">
            <div className="self-stretch text-center text-[#0a0a08] text-lg font-semibold font-inter">What's Your Vibe?</div>
            <div className="self-stretch text-center text-[#7a7871] text-sm font-normal font-inter">
              We're discovering topics from your newsletters. Pick a few now to get started—you can always add more later!
            </div>
          </div>
          <div className="self-stretch px-3 pb-3 flex-col justify-start items-start gap-2 flex">
            {isLoading ? (
              <div className="w-full flex justify-center items-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-[#14997e]" />
              </div>
            ) : error ? (
              <div className="w-full flex flex-col items-center gap-2 py-6">
                <div className="text-red-500 text-center text-sm">{error}</div>
                <RefreshButton onClick={fetchCategories} isLoading={refreshLoading} className="px-3 py-1.5" />
              </div>
            ) : categories.length === 0 ? (
              <div className="w-full flex flex-col items-center gap-3 py-6">
                <div className="text-[#7a7871] text-center">
                  <p className="text-sm mb-2 font-medium">🔍 Discovering topics from your newsletters...</p>
                  <p className="text-xs mb-1">Our AI is analyzing your content to find relevant categories.</p>
                  <p className="text-xs">New topics appear as they're discovered • Auto-refreshing in {autoReloadTimer}s</p>
                </div>
                <RefreshButton 
                  onClick={fetchCategories} 
                  isLoading={refreshLoading} 
                  className="!p-2 !px-4 text-[#14997e] hover:bg-[#14997e] hover:text-white border-[#14997e]" 
                />
              </div>
            ) : (
              <>
                {categories.map((row, rowIndex) => (
                  <div key={rowIndex} className="self-stretch grid grid-cols-3 gap-2">
                    {row.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => toggleCategory(category)}
                        className={`w-full border border-[#e7e6e3] border-solid h-8 px-2 py-5 text-[25px] rounded-md justify-center items-center gap-1 flex overflow-hidden transition-colors ${
                          selectedCategories.has(category.id)
                            ? "bg-[#14997e] text-[#f5f4f4]"
                            : "text-[#0a0a08] hover:bg-[#f5f4f4]"
                        }`}
                      >
                        <div className="text-xs font-medium font-inter truncate">{category.name}</div>
                      </button>
                    ))}
                  </div>
                ))}
                <div className="w-full flex flex-col items-center gap-2 mt-6">
                  <div className="text-xs text-[#7a7871] text-center">
                    More topics are being discovered as our AI processes your newsletters
                  </div>
                  <RefreshButton onClick={fetchCategories} isLoading={refreshLoading} />
                </div>
              </>
            )}
          </div>
          <div className="self-stretch p-3 flex-col gap-2">
            <div className="flex justify-between items-center">
              <button
                onClick={() => navigate("/onboarding/intro")}
                className="px-3 py-2 bg-white rounded-md border border-[#e7e6e3] justify-center items-center gap-1.5 flex"
              >
                <ArrowLeft className="w-4 h-4" />
                <div className="text-[#1b1a17] text-sm font-medium font-inter">Back</div>
              </button>
              <button
                onClick={() => navigate("/onboarding/generating-summary")}
                disabled={selectedCategories.size === 0}
                className={`px-5 py-2.5 rounded-md justify-center items-center gap-1.5 flex transition-colors ${
                  selectedCategories.size === 0 
                    ? "bg-gray-300 cursor-not-allowed" 
                    : "bg-[#14997e] hover:bg-[#128b73]"
                }`}
              >
                <div className={`text-base font-medium font-inter ${
                  selectedCategories.size === 0 ? "text-gray-500" : "text-[#f9f9f9]"
                }`}>
                  {selectedCategories.size === 0 ? "Select topics to continue" : "Generate My Summary"}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectCategories;
